import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { NgControl, FormGroup } from '@angular/forms';
import { EMPTY, Subject, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { AgentService, AgentFormHint } from '../services/agent.service';
import { AiHintStateService } from '../services/ai-hint-state.service';

@Directive({
  selector: '[appAiHint]'
})
export class AiHintDirective implements OnInit, OnDestroy {
  /** Label shown in the hint panel (e.g. "Owner First Name") */
  @Input('appAiHint') fieldLabel = '';

  /** Pass the parent FormGroup to give the AI full form context */
  @Input() aiHintForm?: FormGroup;

  private blur$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    private hintState: AiHintStateService,
    private agentService: AgentService,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  private isDisabledOrReadonly(): boolean {
    const el = this.el.nativeElement as HTMLInputElement;
    return el.readOnly || el.disabled || el.type === 'hidden'
      || this.ngControl?.control?.disabled === true;
  }

  @HostListener('blur')
  onBlur(): void {
    if (this.isDisabledOrReadonly()) return;
    const value: string = this.ngControl?.value ?? (this.el.nativeElement as HTMLInputElement).value ?? '';
    if (value?.toString().trim()) {
      this.blur$.next(value.toString());
    }
  }

  ngOnInit(): void {
    // Also react to programmatic value changes (e.g. datepicker patchValue after blur).
    // Guard: only fire when the field is NOT focused and not readonly/disabled.
    const programmaticChanges$ = this.ngControl?.control?.valueChanges.pipe(
      filter(() => !this.isDisabledOrReadonly()),
      filter(() => document.activeElement !== this.el.nativeElement),
      filter((v: string | null): v is string => !!v?.toString().trim())
    ) ?? EMPTY;

    merge(this.blur$, programmaticChanges$).pipe(
      filter((v): v is string => !!v?.toString().trim()),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        this.hintState.setLoading(this.fieldLabel);
        return this.agentService.getFormHint(this.fieldLabel, value.toString(), this.buildContext());
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: result => this.hintState.setHint(this.fieldLabel, this.reconcilePhoneError(result)),
      error: () => this.hintState.clearLoading()
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Phone fields are validated deterministically by the ten/elevenDigitPhoneNumberValidator
   * (reflected in the field's green/red state). The AI Form Assistant also checks phones, but
   * an LLM can occasionally miscount digits or show a stale verdict — in either direction:
   * rejecting a valid number, or accepting an invalid one. Reconcile both ways so the hint
   * shown always matches the field's actual validity. Scoped to phone fields only.
   */
  private reconcilePhoneError(result: AgentFormHint): AgentFormHint {
    const isPhoneField = this.fieldLabel?.toLowerCase().includes('phone');
    if (!isPhoneField) return result;

    const control = this.ngControl?.control;
    const controlIsValid = control?.valid === true;

    if (controlIsValid && result?.severity === 'error') {
      return { hint: 'Accepted', severity: 'info' };
    }

    if (!controlIsValid && result?.severity !== 'error') {
      const errors = control?.errors ?? {};
      if (errors['invalidElevenDigitPhoneNumber']) {
        return { hint: 'Must be exactly 11 digits starting with 1 (e.g. 1-516-519-9199).', severity: 'error' };
      }
      if (errors['invalidTenDigitPhoneNumber']) {
        return { hint: 'Must be exactly 10 digits with area code not starting with 0 or 1 (e.g. 516-519-9199).', severity: 'error' };
      }
    }

    return result;
  }

  private buildContext(): Record<string, string> {
    if (!this.aiHintForm) return {};
    const raw = this.aiHintForm.getRawValue() ?? {};
    const snapshot: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v && typeof v === 'string' && v.trim()) snapshot[k] = v;
    }
    return snapshot;
  }
}
